import {
  EdgeProps,
  getEdgeCenter,
  Position,
  useInternalNode,
} from '@xyflow/react';
import { useDnD } from '../../context/DnDContext';
import { useEffect, useMemo, useState } from 'react';
import { getHandleCenter } from './utils';
import { useDragMonitor } from '../../store/dragMonitorStore';
import useModifierKeyPressed from '../../shared/hooks/useModifierKeyPressed';
import { useDiagramStore } from '@diagram/ui/hooks/diagram-store.hook';
import { makeNode } from '@diagram/model/node.factory';
import { v4 as uuidv4 } from 'uuid';
import { NodeId } from '@diagram/model/node.model';
import { EdgeId } from '@diagram/model/edge.model';

interface InsertEdgeBaseProps extends EdgeProps {
  getPath: (params: {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    sourcePosition: Position;
    targetPosition: Position;
  }) => [string, number, number, number, number];
}

const InsertEdgeBase = ({
  id,
  source,
  target,
  style,
  data,
  getPath,
  markerEnd,
  sourceHandleId,
  targetHandleId,
}: InsertEdgeBaseProps) => {
  const insertNodeOnEdge = useDiagramStore((s) => s.node.insertNodeOnEdge);
  const updateEdgeLabel = useDiagramStore((s) => s.edge.updateEdgeLabel);

  // const markerEnd = useMemo(() => {
  //   return {
  //     type: MarkerType.Arrow,
  //     width: 10,
  //     height: 10,
  //     color: '#000000',
  //   };
  // }, []);
  const dndContext = useDnD();
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [height, setHeight] = useState<number>(20);
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const { position: draggingPos, setHoveredEdge } = useDragMonitor();
  const isModifierPressed = useModifierKeyPressed();

  useEffect(() => {
    const handleDragStart = () => setIsDragging(true);
    const handleDragEnd = () => setIsDragging(false);
    window.addEventListener('dragstart', handleDragStart);
    window.addEventListener('dragend', handleDragEnd);
    return () => {
      window.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('dragend', handleDragEnd);
    };
  }, []);

  useEffect(() => {
    if (!draggingPos) return setIsDragging(false);

    const dx = draggingPos.x - midX;
    const dy = draggingPos.y - midY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 50) {
      setHoveredEdge(id);
      setIsHovering(true);
    }

    setIsDragging(dist < 50); // Threshold distance
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingPos]);

  // Always call hooks at the top level
  // const edgeParams = useMemo(() => {
  //   if (!sourceNode || !targetNode) return null;
  //   return getEdgeParams(sourceNode, targetNode);
  // }, [sourceNode, targetNode]);

  // useEffect(() => {
  //   if (edgeParams) {
  //     updateEdgeDataAction(id, {
  //       sourcePosition: edgeParams.sourcePos,
  //       targetPosition: edgeParams.targetPos,
  //     });
  //   }
  //   //eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [edgeParams]);

  const sourceCenter = sourceNode
    ? getHandleCenter(sourceNode, 'source', sourceHandleId)
    : null;
  const targetCenter = targetNode
    ? getHandleCenter(targetNode, 'source', targetHandleId)
    : null;

  const mid = useMemo(() => {
    if (!sourceCenter || !targetCenter) return { x: 0, y: 0 };
    const [x, y] = getEdgeCenter({
      sourceX: sourceCenter.x,
      sourceY: sourceCenter.y,
      targetX: targetCenter.x,
      targetY: targetCenter.y,
    });
    return { x, y };
  }, [sourceCenter, targetCenter]);

  // console.log('mid', mid);

  const foreignObjectDims = useMemo(() => {
    if (!sourceCenter || !targetCenter) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    return {
      height: isDragging ? 40 : height,
      width: isDragging ? 80 : 100,
      x: mid.x - (isDragging ? 40 : 50),
      y: mid.y - (isDragging ? 20 : height / 2),
    };
  }, [isDragging, height, mid, sourceCenter, targetCenter]);

  if (!sourceCenter || !targetCenter) return null;

  const sx = sourceCenter.x;
  const sy = sourceCenter.y;
  const tx = targetCenter.x;
  const ty = targetCenter.y;
  const sourcePos = sourceCenter.position;
  const targetPos = targetCenter.position;

  const [midX, midY] = getEdgeCenter({
    sourceX: sx,
    sourceY: sy,
    targetX: tx,
    targetY: ty,
  });

  const [edgePath] = getPath({
    sourceX: sx,
    sourceY: sy,
    targetX: tx,
    targetY: ty,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
  });

  const adjustX = midX - dndContext?.data?.width / 2;
  const adjustY = midY - dndContext?.data?.height / 2;

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (dndContext?.data?.type !== 'shape') return;
    insertNodeOnEdge(
      id,
      makeNode({
        id: uuidv4() as NodeId,
        type: 'shape',
        width: dndContext.data.width,
        height: dndContext.data.height,
        x: adjustX,
        y: adjustY,
        data: {
          shape: dndContext.data.shape,
          color: '#000000',
          label: 'New Node',
          index: 100,
        },
      })
    );
    setIsDragging(false);
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;
    el.style.height = 'auto';
    const newHeight = el.value.trim() ? el.scrollHeight : 20;

    el.style.height = `${newHeight}px`;
    setHeight(newHeight);
    updateEdgeLabel(id as EdgeId, el.value);
  };

  return (
    <>
      <path
        d={edgePath}
        stroke="transparent"
        strokeWidth={20}
        fill="none"
        pointerEvents="stroke"
        data-id={id}
        className="react-flow__edge-interaction hover:cursor-path"
      />
      <path
        id={id}
        d={edgePath}
        markerEnd={markerEnd}
        className="react-flow__edge-path"
        pointerEvents={isDragging ? 'none' : 'all'}
        style={style}
      />
      <defs>
        {markerEnd === 'arrow' && (
          <marker
            id="arrow"
            markerWidth="10"
            markerHeight="10"
            refX="10"
            refY="5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,10 L10,5 z" fill="#000000" />
          </marker>
        )}
      </defs>
      <foreignObject
        x={foreignObjectDims.x}
        y={foreignObjectDims.y}
        width={100}
        height={foreignObjectDims.height}
      >
        {isDragging ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => {
              setHoveredEdge(id);
              setIsHovering(true);
            }}
            onDragLeave={() => {
              setHoveredEdge(null);
              setIsHovering(false);
            }}
            className={`px-2 border-dashed border rounded-md ${
              isHovering
                ? 'border-blue-300 bg-gray-200'
                : 'border-gray-300 bg-gray-100'
            }`}
            style={{
              width: '80px',
              height: '40px',
              display: 'flex',
              pointerEvents: 'all',
            }}
          />
        ) : (
          <div
            className={`flex justify-center items-center transition-opacity ${
              typeof data?.label === 'string' && data.label.trim()
                ? 'opacity-100'
                : isModifierPressed
                ? 'opacity-0'
                : 'opacity-0 hover:opacity-100'
            }`}
          >
            <textarea
              value={typeof data?.label === 'string' ? data.label : ''}
              placeholder="Add label..."
              disabled={isModifierPressed}
              className="text-xs text-center border border-gray-300 rounded px-px py-0.5 bg-white resize-none w-full focus:outline-none focus:border-black"
              rows={1}
              onMouseDown={(e) => {
                if (e.shiftKey) {
                  e.preventDefault();
                }
              }}
              style={{
                overflow: 'hidden',
                minHeight: '20px',
                userSelect: 'none',
                height: `${height}px`,
                width: `${
                  (typeof data?.label === 'string' && data?.label
                    ? data.label.length
                    : 10) + 2
                }ch`,
              }}
              onInput={handleInput}
            />
          </div>
        )}
      </foreignObject>
      ;
    </>
  );
};

export default InsertEdgeBase;
